import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { DrawerFormWithNote } from '@/components/note-form';
import { DEFAULT_CURRENCY } from '@/constants';
import { PERMISSION_OBJ_CV } from '@/constants/permissions';
import { campaignStore, counterpartyStore, currencyStore, cvStore } from '@/stores';
import { NoteObjectType } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';
import { buildPrice } from '@/utils/formatters';

import { normalizedData } from '../utils';
import { getFormFields } from './setup';

const entity = 'cvs';
const store = cvStore;

export const CVForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === `base-layout.${entity}.create`;
  const { cvId: itemId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: any) => id === +itemId);

  const { loading } = useMassUpdateCashedStores([campaignStore, counterpartyStore, currencyStore]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const initValues =
    !isCreate && foundItem
      ? {
          ...foundItem,
          campaignId: foundItem.campaign?.id,
          counterpartyId: foundItem.counterparty?.id,
          period: foundItem.period && moment(foundItem.period.from),
          price: foundItem.price?.amount,
          priceCurrency: foundItem.price?.currency.id || DEFAULT_CURRENCY,
          unlockingDate: foundItem.unlockingDate && moment(foundItem.unlockingDate),
        }
      : {};

  const title = isCreate ? getTranslatedString('cvs.create') : getTranslatedString('cvs.cv-number', { 0: itemId });

  const formFields = getFormFields();
  const allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_CV);

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => {
      values.price = buildPrice(
        values.price ? values.priceCurrency : undefined,
        values.price,
        DEFAULT_CURRENCY,
        undefined,
      );

      return api[entity].create(normalizedData(values)).source.then(({ data }: any) => store.addItem(data));
    },
    update: (values: any) => {
      values.price = buildPrice(
        values.priceCurrency,
        values.price,
        foundItem.price?.currency.id || DEFAULT_CURRENCY,
        foundItem.price?.amount,
      );

      return api[entity]
        .update(foundItem.id, normalizedData(values))
        .source.then(({ data }: any) => store.updateItem(data));
    },
  });

  return (
    <DrawerFormWithNote
      objectType={NoteObjectType.CV}
      resourceId={itemId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || (!isCreate && !foundItem)}
    />
  );
});
